import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../../styles/blog-overrides.scss';

const BlogCard = ({ post, index = 0 }) => {
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  // Generate a fallback description if none is provided
  const getDescription = () => {
    if (post.description && post.description.trim()) {
      return post.description;
    }
    
    // Fallback descriptions based on tags or content
    if (post.tags && post.tags.length > 0) {
      return `Explore insights about ${post.tags.join(', ')}. Read more to discover valuable information and updates.`;
    }
    
    return 'Discover valuable insights and updates. Click to read the full article and learn more.';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ 
        scale: 1.03,
        y: -8,
        transition: { duration: 0.3 }
      }}
      className="blog-card group cursor-pointer h-full"
    >
      <Link to={`/blog/${post.slug}`} className="block h-full" aria-label={`Read more about ${post.title}`}>
        <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-premium border border-accent/10 overflow-hidden transition-all duration-500 hover:shadow-premium-xl hover:border-accent/20 h-full flex flex-col">
          {/* Enhanced Featured Image */}
          {post.featuredImage && (
            <div className="relative h-48 sm:h-56 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10"></div>
              <img
                src={post.featuredImage.url}
                alt={post.featuredImage.alt || post.title}
                className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                loading="lazy"
                onError={handleImageError}
              />
              {/* App Release Badge */}
              {post.appRelease && (
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-accent text-background-dark px-2 sm:px-4 py-1 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold shadow-lg backdrop-blur-sm">
                  {post.appRelease.appName} v{post.appRelease.version}
                </div>
              )}
              {/* Reading Time Badge */}
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 bg-slate-800/80 backdrop-blur-sm text-slate-200 px-2 sm:px-3 py-1 rounded-lg text-xs font-medium">
                📖 5 min read
              </div>
            </div>
          )}
          
          {/* Enhanced Content */}
          <div className="p-4 sm:p-6 flex-1 flex flex-col">
            {/* Tags with Enhanced Styling */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                {post.tags.slice(0, 3).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="px-2 sm:px-3 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-full hover:bg-accent/20 transition-all duration-300 border border-accent/20"
                  >
                    {tag}
                  </span>
                ))}
                {post.tags.length > 3 && (
                  <span className="px-2 sm:px-3 py-1 bg-slate-700/50 text-slate-400 text-xs font-semibold rounded-full border border-accent/10">
                    +{post.tags.length - 3} more
                  </span>
                )}
              </div>
            )}
            
            {/* Enhanced Title */}
            <h3 className="text-lg sm:text-xl font-bold text-slate-100 mb-3 sm:mb-4 group-hover:text-accent transition-colors duration-300 leading-tight flex-1">
              {post.title}
            </h3>
            
            {/* Enhanced Description */}
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 flex-1">
              {truncateText(getDescription())}
            </p>
            
            {/* Enhanced Meta Information */}
            <div className="mt-auto">
              <div className="flex items-center justify-between text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-accent rounded-full flex items-center justify-center text-background-dark text-xs font-bold">
                    {post.author ? post.author.charAt(0).toUpperCase() : 'A'}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-300 text-xs sm:text-sm">{post.author || 'Anonymous'}</span>
                    <div className="text-xs text-slate-500">{formatDate(post.publishedAt)}</div>
                  </div>
                </div>
                
                {/* App Release Badge */}
                {post.appRelease && (
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-accent font-semibold text-xs bg-accent/10 px-2 py-1 rounded-full">
                      📱 {post.appRelease.appName}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-accent/10">
                <span className="text-accent font-semibold text-xs sm:text-sm group-hover:text-accent/90 transition-colors">
                  Read More
                </span>
                <div className="flex items-center space-x-2">
                  {/* Download Button for App Release */}
                  {post.appRelease && post.appRelease.downloadLink && (
                    <a
                      href={post.appRelease.downloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="download-button inline-flex items-center space-x-1 bg-accent hover:bg-accent/90 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-premium border border-accent/30 hover:border-accent/50 shadow-sm text-background-dark"
                    >
                      <svg 
                        className="w-3 h-3 text-background-dark" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-medium text-background-dark">
                        {post.appRelease.appName}
                      </span>
                    </a>
                  )}
                  {/* Read More Arrow */}
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-accent rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-2 h-2 sm:w-3 sm:h-3 text-background-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default BlogCard; 